import { RefObject, useCallback } from "react";

import BulkActions from "components/BulkActions";
import {
  BulkActionItemID,
  BulkActionsStep,
  BulkActionsVariant,
} from "components/BulkActions/types";
import useBulkActions from "components/BulkActions/useBulkActions";
import BulkActionsFloatingBar from "components/BulkActions/FloatingBar";
import BulkActionsDrawer from "components/BulkActions/Drawer";
import BulkActionsFloatingBarHeader from "components/BulkActions/FloatingBar/Header";
import BulkActionsFloatingBarSteps from "components/BulkActions/FloatingBar/Steps";
import BulkActionsDrawerActionSteps from "components/BulkActions/Drawer/ActionSteps";
import BulkActionsDrawerActionStepsHeader from "components/BulkActions/Drawer/ActionSteps/Header";
import BulkActionsDrawerActionStepsInfoBanner from "components/BulkActions/Drawer/ActionSteps/InfoBanner";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useAnalytics from "hooks/useAnalytics";
import BulkActionsPatternsActionsList from "components/BulkActionsPatterns/ActionsList";
import { Module, Stack } from "types/generated";

import { EntityChangeWithId } from "../types";
import useChangesBulkActions from "./useChangesBulkActions";
import { ChangesBulkActionsAnalyticsPayload } from "./types";
import ChangesBulkActionsConfirmationView from "./ConfirmationView";
import ChangesBulkActionsSelectedItemsView from "./SelectedItemsView";
import ChangesBulkActionsConfirmActionForm from "./ConfirmActionForm";

type ChangesBulkActionsProps = {
  listRef: RefObject<HTMLElement | null>;
  selectedSet: Set<BulkActionItemID>;
  entityChangesMap: Map<BulkActionItemID, EntityChangeWithId>;
  onBulkResetAll: () => void;
  onItemDismiss: (id: BulkActionItemID) => void;
  onReplan: () => void;
  isProcessing: boolean;
  stack: Stack | Module;
  onHeightChange?: (height: number) => void;
};

const ChangesBulkActions = ({
  listRef,
  selectedSet,
  entityChangesMap,
  onBulkResetAll,
  onItemDismiss,
  onReplan,
  isProcessing,
  stack,
  onHeightChange,
}: ChangesBulkActionsProps) => {
  const selectedItemsCount = selectedSet.size;

  const changesBulkActions = useChangesBulkActions(stack);
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageStack.StacksRunChanges,
  });
  const {
    selectedBulkAction,
    resetSelectedAction,
    availableActions,
    itemsPerAction,
    actionsPerItem,
    selectedEntityItems,
    applicableSelectedEntityItems,
    skippedSelectedEntityItems,
  } = useBulkActions({
    actions: changesBulkActions,
    selectedSet,
    entityItemsMap: entityChangesMap,
  });

  const handleClose = useCallback(() => {
    onBulkResetAll();
    resetSelectedAction();
  }, [onBulkResetAll, resetSelectedAction]);

  const handleOnConfirm = useCallback(
    (analyticsPayload: ChangesBulkActionsAnalyticsPayload) => {
      if (selectedBulkAction) {
        const items = itemsPerAction.get(selectedBulkAction.key) || [];
        const skippedItems = skippedSelectedEntityItems.map((item) => item.id);

        onReplan();

        trackSegmentAnalyticsEvent("Bulk Actions - Action Confirmed", {
          applicableCount: items?.length || 0,
          notApplicableCount: skippedItems.length,
          action: selectedBulkAction.key,
          ...analyticsPayload,
        });
      }
    },
    [
      itemsPerAction,
      onReplan,
      selectedBulkAction,
      skippedSelectedEntityItems,
      trackSegmentAnalyticsEvent,
    ]
  );

  const handleOnCancel = (variant: BulkActionsVariant) => () => {
    if (selectedBulkAction) {
      resetSelectedAction();
      const items = itemsPerAction.get(selectedBulkAction.key);
      trackSegmentAnalyticsEvent("Bulk actions - Action Cancelled", {
        applicableCount: items?.length || 0,
        notApplicableCount: skippedSelectedEntityItems.length,
        action: selectedBulkAction.key,
        view: variant,
      });
    }
  };

  const handleContinueWith = useCallback(() => {
    resetSelectedAction();
  }, [resetSelectedAction]);

  return (
    <BulkActions
      onContinueWith={handleContinueWith}
      selectedItemsCount={selectedItemsCount}
      onClose={handleClose}
      isExecutingBulkActions={false}
      skipResults={true}
    >
      <BulkActionsFloatingBar virtualizedListContainerRef={listRef} onHeightChange={onHeightChange}>
        <BulkActionsFloatingBarHeader
          selectedItemsCount={selectedItemsCount}
          applicableItemsCount={
            selectedBulkAction?.key ? itemsPerAction.get(selectedBulkAction.key)?.length : 0
          }
          analyticsPage={AnalyticsPageStack.StacksRunChanges}
        />
        <BulkActionsFloatingBarSteps>
          {!selectedBulkAction && (
            <BulkActionsPatternsActionsList
              variant={BulkActionsVariant.FloatingBar}
              availableActions={availableActions}
              onEmptyActionsCancel={handleClose}
            />
          )}

          {selectedBulkAction && (
            <ChangesBulkActionsConfirmActionForm
              variant={BulkActionsVariant.FloatingBar}
              action={selectedBulkAction}
              isProcessing={isProcessing}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.FloatingBar)}
            />
          )}
        </BulkActionsFloatingBarSteps>
      </BulkActionsFloatingBar>

      <BulkActionsDrawer>
        <BulkActionsDrawerActionSteps>
          <BulkActionsDrawerActionStepsHeader
            analyticsPage={AnalyticsPageStack.StacksRunChanges}
            step={
              !selectedBulkAction
                ? BulkActionsStep.ChooseAction
                : BulkActionsStep.ActionConfirmation
            }
          />
          <BulkActionsDrawerActionStepsInfoBanner />

          {!selectedBulkAction && (
            <ChangesBulkActionsSelectedItemsView
              items={selectedEntityItems}
              onItemDismiss={onItemDismiss}
              availableActions={availableActions}
              actionsPerItem={actionsPerItem}
              onEmptyActionsCancel={handleClose}
            />
          )}
          {selectedBulkAction && (
            <ChangesBulkActionsConfirmationView
              applicableItems={applicableSelectedEntityItems}
              skippedItems={skippedSelectedEntityItems}
              onItemDismiss={onItemDismiss}
              selectedBulkAction={selectedBulkAction}
              isProcessing={isProcessing}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.Drawer)}
            />
          )}
        </BulkActionsDrawerActionSteps>
      </BulkActionsDrawer>
    </BulkActions>
  );
};

export default ChangesBulkActions;
